import { useState } from "react";
import { UserRound, Activity, BarChart3, Megaphone, Inbox, Camera, NotebookPen } from "lucide-react";
import AccountingIcon from "@/components/icons/AccountingIcon";
import { useAuth } from "@/context/AuthContext";
import StatsTab from "@/components/dashboard/StatsTab";
import OperationsTab from "@/components/dashboard/OperationsTab";
import BroadcastTab from "@/components/dashboard/BroadcastTab";
import RequestsTab from "@/components/dashboard/RequestsTab";
import RipOutsTab from "@/components/dashboard/RipOutsTab";
import ReferenceTab from "@/components/dashboard/ReferenceTab";
import ReportsTab from "@/components/dashboard/ReportsTab";
import AccountingTab from "@/components/dashboard/accounting/AccountingTab";

// Reports is owner/superadmin-only while it's being rebuilt — everyone else
// doesn't even see the pill. Live sections (bank balance) sit on top; the
// rest stays a placeholder.

/**
 * HomeTab — for privileged users (owner / superadmin / admin-with-hours)
 * this wraps StatsTab + OperationsTab + ReportsTab in sub-tabs; owners/admins
 * (superadmin counts as admin) also get Broadcast. On phones the sub-tabs
 * collapse to icon buttons (text names return at md / tablet and up) so the
 * bar never overflows the screen and causes horizontal wobble.
 */
const HomeTab = () => {
  const { user } = useAuth();
  const perms = user?.permissions || {};
  const role = user?.role || "";
  const isPrivileged = role === "owner" || role === "superadmin" || role === "admin" || role === "manager" || !!user?.manage_users || !!perms.hours;
  const canBroadcast = role === "owner" || role === "superadmin" || role === "admin" || !!user?.manage_users;
  // Reports — owner/superadmin ONLY while it's rebuilt (was: managers and
  // billing/projects-permission users could see the old profitability page).
  const canSeeReports = ["owner", "superadmin", "admin", "manager"].includes(role);
  // Rip-outs log is staff-only; employees submit from the Requests tab.
  const isStaff = role === "owner" || role === "superadmin" || role === "admin" || role === "manager" || !!user?.manage_users;

  // Remember the open sub-tab across refreshes (per browser tab session), so
  // a page refresh — including the auto-reload after a new deploy — lands back
  // where you were instead of resetting to My Stats.
  const SUB_KEY = "home_subtab";
  const [sub, setSubState] = useState(() => {
    try {
      return sessionStorage.getItem(SUB_KEY) || "stats";
    } catch { return "stats"; }
  });
  const setSub = (key) => {
    try { sessionStorage.setItem(SUB_KEY, key); } catch { /* private mode */ }
    setSubState(key);
  };

  // Inactive employees: home tabs are disabled — render a blank page.
  if ((user?.employee_status || "Active") !== "Active") {
    return <div className="min-h-[50vh]" data-testid="home-inactive-blank" />;
  }

  // Every pill is uniform in every theme: icon on phones, the word on
  // tablet and larger (Accounting included — no lone symbol in a row of words).
  const pill = (key, label, Icon) => (
    <button
      key={key}
      type="button"
      onClick={() => setSub(key)}
      data-testid={`home-subtab-${key}`}
      aria-label={label}
      title={label}
      className={`flex min-w-0 flex-1 items-center justify-center gap-1.5 rounded-md px-3 py-2 font-mono text-[11px] uppercase tracking-widest transition-colors md:flex-none md:px-4 ${
        sub === key
          ? "bg-[#1a3fd1] text-white shadow"
          : "border border-white/10 bg-white/[0.02] text-white/70 hover:border-white/25 hover:text-white"
      }`}
    >
      {/* phones: icon only · tablet and larger: name only */}
      <Icon className="h-4 w-4 md:hidden" />
      <span className="hidden md:inline">{label}</span>
    </button>
  );

  // Every role gets the Requests sub-tab; plain employees get a slim bar
  // (My stats + Requests) instead of the bare stats page.
  if (!isPrivileged) {
    return (
      <div className="min-w-0 space-y-4 overflow-x-hidden">
        <div
          className="flex min-w-0 max-w-full gap-2 overflow-x-auto rounded-lg border border-white/10 bg-white/[0.02] p-1.5"
          role="tablist"
          aria-label="Home sub-tabs"
          data-testid="home-subtabs"
        >
          {pill("stats", "My stats", UserRound)}
          {pill("requests", "Requests", Inbox)}
          {pill("reference", "Notes", NotebookPen)}
          {pill("accounting", "Finances", AccountingIcon)}
        </div>
        {sub === "requests" ? <RequestsTab /> : sub === "reference" ? <ReferenceTab /> : sub === "accounting" ? <AccountingTab /> : <StatsTab />}
      </div>
    );
  }

  return (
    <div className="min-w-0 space-y-4 overflow-x-hidden">
      <div
        className="flex min-w-0 max-w-full gap-2 overflow-x-auto rounded-lg border border-white/10 bg-white/[0.02] p-1.5"
        role="tablist"
        aria-label="Home sub-tabs"
        data-testid="home-subtabs"
      >
        {pill("stats", "My stats", UserRound)}
        {pill("operations", "Operations", Activity)}
        {canSeeReports && pill("reports", "Reports", BarChart3)}
        {pill("requests", "Requests", Inbox)}
        {pill("reference", "Notes", NotebookPen)}
        {pill("accounting", "Finances", AccountingIcon)}
        {isStaff && pill("ripouts", "Rip-outs", Camera)}
        {canBroadcast && pill("broadcast", "Broadcast", Megaphone)}
      </div>
      {sub === "requests" ? <RequestsTab /> : sub === "reference" ? <ReferenceTab /> : sub === "accounting" ? <AccountingTab /> : sub === "stats" ? <StatsTab /> : sub === "operations" ? <OperationsTab /> : sub === "ripouts" && isStaff ? <RipOutsTab /> : sub === "broadcast" && canBroadcast ? <BroadcastTab /> : sub === "reports" && canSeeReports ? <ReportsTab /> : <StatsTab />}
    </div>
  );
};

export default HomeTab;
